import { sdkLogger } from '~/utils';

/**
 * SDK调用上下文
 */
interface SdkContext {
    // SDK调用结果
    result: any;
    // SDK类型
    sdkType: string;
    // SDK API
    sdkApi: string;
    // 日志前缀
    prefix?: string;
}

/**
 * 设置超时时间，如果超过 60 秒，则记录超时日志
 * 60 秒的超时时间没有明确参考，但基本上网络正常情况下，60 秒内绝大部分 API 调用应该都会返回结果
 */
const SDK_API_TIME_LIMIT = 60_000;

/**
 * 处理SDK调用结果的公共函数
 * @param {SdkContext} sdkContext SDK调用上下文
 * @returns {any} 处理后的结果
 */
const handleSdkResult = (sdkContext: SdkContext) => {
    const { result, sdkType, sdkApi, prefix = '' } = sdkContext;
    const logPrefix = `${sdkType} ${sdkApi} ${prefix}`;
    if (result instanceof Promise) {
        return new Promise((resolve, reject) => {
            /**
             * 在某些场景下，调用jssdk API时，可能既不会走到 onSuccess 回调，也不会走到 onFail 回调，
             * 此时给 API 包裹的 promise 就会一直是 pending 状态，从而无法获取成功或失败的结果。
             *
             * 经过和测试讨论，目前超时概率较大的的是图片上传（单张最大 5MB * 最多9张），加上用户设备差异性和网络状况等原因
             * 超时时限其实不好控制，且目前超时后阻塞用户的情况比较少，而图片上传场景使用概率大得多，所以综合考虑下采取以下方案：
             * 超时后，仅记录超时日志，而不终止异步任务
             * @see FXD-72288
             */
            const timer = setTimeout(() => {
                const timeoutErrMsg = `${logPrefix} get no result after ${SDK_API_TIME_LIMIT} ms`;
                sdkLogger.warn(timeoutErrMsg);
                clearTimeout(timer);
            }, SDK_API_TIME_LIMIT);
            result
                .then((res) => {
                    sdkLogger.debug(`${logPrefix} success`, { res });
                    resolve(res);
                })
                .catch((err) => {
                    sdkLogger.error(`${logPrefix} fail`, { err });
                    reject(err);
                })
                .finally(() => {
                    clearTimeout(timer);
                });
        });
    }
    sdkLogger.debug(`${logPrefix} result not promise`, { res: result });
    return result;
};

/**
 * 日志装饰器，用于统一记录sdk的调用日志
 * @param type sdk类型，默认为 this.sdkType
 * 注意，如果 jsSdkAuth 和 sdkLog 装饰器同时使用，
 * 需要将 jsSdkAuth 装饰器放在 sdkLog 装饰器之前，以确保调用顺序符合预期
 */
export function sdkLog(type = 'jssdk-model') {
    /**
     * @param _ 类的实例
     * @param propKey 类的属性名
     * @param descriptor 类的属性描述符
     */
    return function (_: any, propKey: string, descriptor: PropertyDescriptor) {
        const originalJsSdkApi = descriptor.value;
        type OriginalApiType = typeof originalJsSdkApi;
        descriptor.value = function (
            ...args: Parameters<OriginalApiType>
        ): ReturnType<OriginalApiType> {
            const sdkType = type || this.sdkType;
            sdkLogger.debug(`${sdkType} ${propKey} invoked`, { args });
            const result = originalJsSdkApi.apply(this, args);
            return handleSdkResult({
                result,
                sdkType,
                sdkApi: propKey
            });
        };

        return descriptor;
    };
}
